import {
  collection,
  doc,
  getDoc,
  increment,
  serverTimestamp,
  writeBatch,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Пишет документ вместе со сдвигом анти-спам счётчика в ОДНОЙ batch-операции.
 *
 * Firestore-правила принимают заказ (и вызов официанта) только если счётчик
 * сдвинут этой же операцией — см. firestore.rules → Rate Limiting. Записать
 * документ отдельно и обойти лимит нельзя: batch атомарен, а сам счётчик
 * умеет двигаться лишь вперёд.
 *
 * windowMinutes обязан совпадать с окном в правилах, иначе клиент будет
 * пытаться продлить окно, которое сервер уже считает истёкшим.
 */
export async function writeWithRateLimit(opts: {
  collectionName: string;
  limitCollection: string;
  uid: string;
  data: DocumentData;
  windowMinutes: number;
}): Promise<string> {
  const { collectionName, limitCollection, uid, data, windowMinutes } = opts;
  const targetRef = doc(collection(db, collectionName));
  const limitRef = doc(db, limitCollection, uid);

  // Смотрим счётчик: продлеваем текущее окно или открываем новое.
  // Офлайн чтение может не дойти — тогда считаем окно новым, решение всё
  // равно перепроверит сервер при синхронизации.
  let startNewWindow = true;
  try {
    const snap = await getDoc(limitRef);
    if (snap.exists()) {
      const windowStart = snap.data()?.windowStart;
      const startedMs =
        typeof windowStart?.toMillis === 'function' ? windowStart.toMillis() : 0;
      startNewWindow = Date.now() - startedMs > windowMinutes * 60_000;
    }
  } catch {
    // нет связи и нет кеша — не падаем, пусть решает сервер
  }

  const batch = writeBatch(db);
  batch.set(targetRef, data);
  batch.set(
    limitRef,
    startNewWindow
      ? { count: 1, lastAt: serverTimestamp(), windowStart: serverTimestamp() }
      : // windowStart намеренно не трогаем: merge сохранит прежний, и правило
        // увидит, что окно то же самое
        { count: increment(1), lastAt: serverTimestamp() },
    { merge: true }
  );
  await batch.commit();
  return targetRef.id;
}

/**
 * Правила отклонили запись. На гостевых путях (заказ, вызов официанта)
 * единственная причина отказа — анти-спам лимит, поэтому показываем
 * человеку «слишком часто», а не «ошибка доступа».
 */
export function isRateLimited(error: unknown): boolean {
  return (error as { code?: string } | null)?.code === 'permission-denied';
}
